import React, { useEffect, useState } from "react";
import { View, StyleSheet, ActivityIndicator } from "react-native";
import { useToast } from "../../../contexts/Toast/ToastProvider";
import { z } from "zod";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import CustomTextInput from "../../../components/common/general/CustomTextInput/CustomTextInput";
import CustomButton from "../../../components/common/general/CustomButton/CustomButton";
import CustomSpacing from "../../../components/common/layout/CustomSpacing/CustomSpacing";
import ImageUpload from "../../../components/common/general/ImageUpload/ImageUpload";
import {
  useUpdateCourseMutation,
  useGetCourseCoreQuery,
  useReGenerateCourseImageMutation,
  StoragePathType,
  Course,
} from "../../../graphql/generated/graphql";
import { PlusCircle, Sparkle } from "@phosphor-icons/react";
import { useAppConfig } from "../../../AppConfigProvider";
import AddCoursePrerequisitesModal from "./AddCoursePrerequisitesModal";
import CoursePreRequisitePill from "./CoursePreRequisitePill";

const courseInfoSchema = z.object({
  title: z.string().min(1, "Full name is required"),
  courseCode: z.string().optional(),
  isDraft: z.boolean().optional(),
  isEnrollmentAvailable: z.boolean().optional(),
  thumbnail: z.string().optional(),
});

type CourseInfoFormData = z.infer<typeof courseInfoSchema>;

const CourseInfoContainer = ({ courseId }: { courseId: string }) => {
  const appConfig = useAppConfig();
  const primaryColor = appConfig.primaryColor;

  const { showToast } = useToast();

  const [isRegeneratingCourseImage, setIsRegeneratingCourseImage] = useState(false);
  const [isAddingPrerequisite, setIsAddingPrerequisite] = useState(false);

  const {
    data: courseData,
    loading,
    error,
    refetch,
  } = useGetCourseCoreQuery({
    variables: {
      id: courseId,
    },
  });

  const course = courseData?.getCourseCore?.data?.[0];

  const [updateCourse] = useUpdateCourseMutation();
  const [reGenerateCourseImage] = useReGenerateCourseImageMutation();

  const [uploadError, setUploadError] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
  } = useForm<CourseInfoFormData>({
    resolver: zodResolver(courseInfoSchema),
    defaultValues: {
      title: "",
      courseCode: "",
      isDraft: false,
      isEnrollmentAvailable: false,
      thumbnail: "",
    },
  });

  useEffect(() => {
    if (course) {
      reset({
        title: course.name || "",
        courseCode: course.course_code || "",
        isDraft: course.isDraft || false,
        isEnrollmentAvailable: course.isEnrollmentAvailable || false,
        thumbnail: course.thumbnail || "",
      });
    }
  }, [course, reset]);

  const handleReGenerateCourseImage = async () => {
    try {
      setIsRegeneratingCourseImage(true);
      await reGenerateCourseImage({ variables: { courseId: course.id } });
      refetch();
    } catch (error) {
      console.error("Error re-generating course image:", error);
    } finally {
      setIsRegeneratingCourseImage(false);
    }
  };

  const handleSave = handleSubmit(async (formData) => {
    try {
      const response = await updateCourse({
        variables: {
          id: course.id,
          course: {
            name: formData.title || "",
            courseCode: formData.courseCode || "",
            isDraft: formData.isDraft || false,
            isEnrollmentAvailable: formData.isEnrollmentAvailable || false,
            thumbnail: formData.thumbnail || "",
          },
        },
      });

      refetch();
      showToast({
        message: "Course updated successfully",
        type: "success",
      });
      reset(formData);
    } catch (error) {
      console.error("Error saving course:", error);
      showToast({
        message: "Failed to save course changes. Please try again.",
        type: "error",
      });
    }
  });

  if (loading || !course) {
    return <CustomText text="Loading..." size="l" weight="ultraBold" style={styles.title} />;
  }

  if (error) {
    return <CustomText text="Error loading course" size="l" weight="ultraBold" style={styles.title} />;
  }

  return (
    <View style={styles.container}>
      <View
        style={{
          flexDirection: "column",
          justifyContent: "flex-start",
          maxWidth: 600,
          gap: 40,
          padding: 40,
          borderWidth: 1,
          borderColor: "#E5E7EB",
          borderRadius: 12,
        }}>
        <View style={styles.form}>
          <View style={styles.formGroup}>
            <CustomText text="Course Title" size="s" weight="ultraBold" style={{}} />
            <CustomSpacing type="vertical" size="s" />
            <Controller
              control={control}
              name="title"
              disabled={true}
              render={({ field: { onChange, value } }) => (
                <CustomTextInput
                  value={value}
                  onChangeText={onChange}
                  placeholder="Course Name"
                  style={{}}
                  error={errors.title?.message}
                  disabled={true}
                />
              )}
            />
          </View>

          <CustomSpacing type="vertical" size="l" />

          <View style={styles.formGroup}>
            <CustomText text="Course Code (Optional)" size="s" weight="ultraBold" style={{}} />
            <CustomSpacing type="vertical" size="s" />
            <Controller
              control={control}
              name="courseCode"
              render={({ field: { onChange, value } }) => (
                <CustomTextInput
                  value={value}
                  onChangeText={onChange}
                  placeholder="ENG101"
                  style={{}}
                  error={errors.courseCode?.message}
                />
              )}
            />
          </View>
          <CustomSpacing type="vertical" size="l" />

          <View style={styles.formGroup}>
            <CustomText text="Course Status" size="s" weight="ultraBold" style={{}} />
            <CustomSpacing type="vertical" size="s" />
            <Controller
              control={control}
              name="isDraft"
              render={({ field: { onChange, value } }) => (
                <View style={{ flexDirection: "row", gap: 12 }}>
                  <CustomButton
                    text="Published"
                    leftIcon={
                      !value ? (
                        <View
                          style={{
                            width: 10,
                            height: 10,
                            borderRadius: 5,
                            backgroundColor: "#22C55E",
                            marginRight: 8,
                          }}
                        />
                      ) : undefined
                    }
                    styleType="light"
                    size="m"
                    style={{
                      borderColor: !value ? "#22C55E" : "#E5E7EB",
                      borderWidth: 2,
                      backgroundColor: "#fff",
                    }}
                    textStyle={{
                      color: !value ? "#111827" : "#6B7280",
                      fontWeight: !value ? "bold" : "normal",
                    }}
                    onPress={() => onChange(false)}
                  />
                  <CustomButton
                    text="Draft"
                    leftIcon={
                      !value ? undefined : (
                        <View
                          style={{ width: 10, height: 10, borderRadius: 5, backgroundColor: "#22C55E", marginRight: 8 }}
                        />
                      )
                    }
                    styleType="light"
                    size="m"
                    style={{
                      borderColor: value ? "#22C55E" : "#E5E7EB",
                      borderWidth: 2,
                      backgroundColor: "#fff",
                    }}
                    textStyle={{
                      color: value ? "#111827" : "#6B7280",
                      fontWeight: value ? "bold" : "normal",
                    }}
                    onPress={() => onChange(true)}
                  />
                </View>
              )}
            />
          </View>

          <CustomSpacing type="vertical" size="l" />

          <View style={styles.formGroup}>
            <CustomText text="Course Enrollment Status" size="s" weight="ultraBold" style={{}} />
            <CustomSpacing type="vertical" size="s" />
            <Controller
              control={control}
              name="isEnrollmentAvailable"
              render={({ field: { onChange, value } }) => (
                <View style={{ flexDirection: "column" }}>
                  <View style={{ flexDirection: "row", gap: 12 }}>
                    <CustomButton
                      text="Enrollment Allowed"
                      leftIcon={
                        value ? (
                          <View
                            style={{
                              width: 10,
                              height: 10,
                              borderRadius: 5,
                              backgroundColor: "#22C55E",
                              marginRight: 8,
                            }}
                          />
                        ) : undefined
                      }
                      styleType="light"
                      size="m"
                      style={{
                        borderColor: value ? "#22C55E" : "#E5E7EB",
                        borderWidth: 2,
                        backgroundColor: "#fff",
                      }}
                      textStyle={{
                        color: value ? "#111827" : "#6B7280",
                        fontWeight: value ? "bold" : "normal",
                      }}
                      onPress={() => onChange(true)}
                    />
                    <CustomButton
                      text="Enrollment Disabled"
                      leftIcon={
                        value ? undefined : (
                          <View
                            style={{
                              width: 10,
                              height: 10,
                              borderRadius: 5,
                              backgroundColor: "#22C55E",
                              marginRight: 8,
                            }}
                          />
                        )
                      }
                      styleType="light"
                      size="m"
                      style={{
                        borderColor: !value ? "#22C55E" : "#E5E7EB",
                        borderWidth: 2,
                        backgroundColor: "#fff",
                      }}
                      textStyle={{
                        color: !value ? "#111827" : "#6B7280",
                        fontWeight: !value ? "bold" : "normal",
                      }}
                      onPress={() => onChange(false)}
                    />
                  </View>
                  <CustomText
                    text="Note: Even if enrollment is disabled, users can still enroll using a valid enrollment link."
                    size="xs"
                    style={styles.helperText}
                    useTranslationText={false}
                  />
                </View>
              )}
            />
          </View>

          <CustomSpacing type="vertical" size="l" />

          <View style={styles.formGroup}>
            <CustomText text="Course Prerequisites (Optional)" size="s" weight="ultraBold" style={{}} />
            <View style={{ flexDirection: "row", gap: 12 }}>
              {course.preRequisites.length > 0 && (
                <View style={{ flexDirection: "row", gap: 12, marginVertical: 12, flexWrap: "wrap" }}>
                  {course.preRequisites
                    ?.filter((prerequisite) => prerequisite.id !== course.id)
                    .map((prerequisite) => (
                      <CoursePreRequisitePill
                        key={prerequisite.id}
                        courseId={course.id}
                        prerequisite={prerequisite as Course}
                        refetchCourse={refetch}
                      />
                    ))}
                </View>
              )}
            </View>
            <CustomButton
              leftIcon={<PlusCircle size={16} weight="bold" color={primaryColor} />}
              text="Add a Prerequisite"
              size="m"
              styleType="primaryTransparentStroke"
              onPress={() => setIsAddingPrerequisite(true)}
              style={{ width: "fit-content" }}
              rightIcon={null}
              textProps={{}}
              useTranslationText={true}
            />
          </View>

          <CustomSpacing type="vertical" size="l" />

          <View style={styles.formGroup}>
            <CustomText text="Course Thumbnail Image" size="s" weight="ultraBold" style={{}} />
            <CustomSpacing type="vertical" size="s" />
            <View style={{ display: "flex", flexDirection: "column" }}>
              <View style={styles.thumbnailImageSection}>
                <Controller
                  control={control}
                  name="thumbnail"
                  render={({ field: { onChange, value } }) => (
                    <ImageUpload
                      value={value}
                      onChange={onChange}
                      storagePath={StoragePathType.Thumbnails}
                      containerStyle={{
                        width: 350,
                        height: 230,
                        borderRadius: 12,
                        borderWidth: 1,
                        borderColor: "#E5E7EB",
                        backgroundColor: "#F3F4F6",
                        borderStyle: "solid",
                      }}
                      imageStyle={{
                        borderRadius: 12,
                        overflow: "visible",
                      }}
                      aspectRatio={[16, 9]}
                      allowsEditing={true}
                      onError={(error) => setUploadError(error)}
                      placeholderContent={
                        <View style={styles.thumbnailImagePlaceholder}>
                          <CustomText
                            text={
                              course.name
                                ?.split(" ")
                                .map((n) => n[0])
                                .join("") || ""
                            }
                            size="l"
                            weight="ultraBold"
                            style={styles.placeholderText}
                            useTranslationText={false}
                          />
                        </View>
                      }
                    />
                  )}
                />
              </View>
              <CustomSpacing type="vertical" size="s" />
              {isRegeneratingCourseImage && (
                <CustomButton
                  leftIcon={<ActivityIndicator size="small" color={primaryColor} />}
                  text="Generating..."
                  size="m"
                  styleType="primaryTransparentStroke"
                  style={{ width: "fit-content" }}
                  disabled={true}
                  onPress={() => {}}
                />
              )}
              {!isRegeneratingCourseImage && (
                <CustomButton
                  leftIcon={<Sparkle size={16} weight="bold" color={primaryColor} />}
                  text="Re-Generate"
                  size="m"
                  styleType="primaryTransparentStroke"
                  onPress={handleReGenerateCourseImage}
                  style={{ width: "fit-content" }}
                  rightIcon={null}
                  textProps={{}}
                  useTranslationText={true}
                />
              )}
              <CustomText text="courseThumbnailImage" size="xs" style={styles.helperText} />
              <CustomText text="JPG or PNG, max 2MB" size="xs" style={styles.helperText} />
              {uploadError && <CustomText text={uploadError} size="xs" style={styles.errorText} />}
            </View>
          </View>
        </View>
        <View style={styles.footer}>
          <CustomButton
            text="Save"
            styleType="primary"
            size="m"
            onPress={handleSave}
            leftIcon={null}
            rightIcon={null}
            style={{
              backgroundColor: !isDirty ? "#E5E7EB" : primaryColor,
              opacity: !isDirty ? 0.5 : 1,
            }}
            textStyle={{
              color: !isDirty ? "#6B7280" : "#FFFFFF",
            }}
            disabled={!isDirty}
            textProps={{}}
            useTranslationText={true}
          />
        </View>
      </View>

      <AddCoursePrerequisitesModal
        visible={isAddingPrerequisite}
        onClose={() => setIsAddingPrerequisite(false)}
        course={course as Course}
        refetchCourse={refetch}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 24,
    backgroundColor: "#fff",
    width: 600,
    marginHorizontal: "auto",
    flexDirection: "column",
  },
  footer: {
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
  },
  title: {
    fontSize: 24,
    color: "#111827",
  },
  form: {
    flex: 1,
    maxWidth: 600,
  },
  formGroup: {
    width: "auto",
  },
  helperText: {
    color: "#6B7280",
    marginTop: 4,
  },
  errorText: {
    color: "#EF4444",
    marginTop: 4,
  },
  thumbnailImageSection: {
    alignItems: "flex-start",
    position: "relative",
    // @ts-expect-error - works for web
    width: "fit-content",
  },
  thumbnailImagePlaceholder: {
    width: "100%",
    height: "100%",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#F3F4F6",
  },
  placeholderText: {
    color: "#6B7280",
    fontSize: 40,
  },
});

export default CourseInfoContainer;
